import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Phone, 
  Mail, 
  MessageCircle, 
  Clock, 
  Users, 
  CheckCircle,
  Calendar,
  Sparkles,
  Building2,
  HardHat,
  Factory,
  Check,
  ArrowLeft,
  ArrowRight
} from "lucide-react";

// Success animation component
function SuccessAnimation({ onClose, t }: { onClose: () => void; t: (key: string) => string }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
    >
      {/* Confetti particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ 
              y: 0, 
              x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
              opacity: 1,
              scale: 0
            }}
            animate={{ 
              y: (typeof window !== 'undefined' ? window.innerHeight : 800) + 100,
              rotate: Math.random() * 720,
              opacity: 0,
              scale: 1
            }}
            transition={{ 
              duration: 2 + Math.random() * 2,
              delay: Math.random() * 0.5,
              ease: "easeOut"
            }}
            className="absolute w-3 h-3 rounded-sm"
            style={{
              backgroundColor: ['hsl(38, 92%, 50%)', 'hsl(222, 47%, 11%)', 'hsl(43, 96%, 56%)', 'hsl(32, 95%, 44%)'][i % 4],
              top: -20,
            }}
          />
        ))}
      </div>

      {/* Success card */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", damping: 15, stiffness: 300, delay: 0.2 }}
        className="bg-card rounded-3xl p-8 md:p-12 shadow-2xl border border-border max-w-md mx-4 text-center relative z-10"
      >
        {/* Checkmark circle */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", damping: 10, stiffness: 200, delay: 0.4 }}
          className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-accent to-accent-dark flex items-center justify-center shadow-lg shadow-accent/30"
        >
          <motion.div
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <Check className="w-10 h-10 text-accent-foreground" strokeWidth={3} />
          </motion.div>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="font-heading text-3xl font-bold text-foreground mb-3"
        >
          {t('success.title')}
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-muted-foreground text-lg mb-6"
        >
          {t('success.message')}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="flex flex-col sm:flex-row gap-3 justify-center"
        >
          <Link to="/library">
            <Button variant="cta" className="gap-2 w-full sm:w-auto">
              {t('success.libraryButton')}
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
            {t('success.closeButton')}
          </Button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

export default function Contact() {
  const { t, i18n } = useTranslation('contact');
  const isRTL = i18n.language === 'he';
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;
  
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation();
  const { ref: bookingRef, isVisible: bookingVisible } = useScrollAnimation();
  const [showSuccess, setShowSuccess] = useState(false);

  const handleFormSuccess = () => {
    setShowSuccess(true);
  };

  const whatHappensItems = t('booking.whatHappens.items', { returnObjects: true }) as string[];
  const audienceItems = t('booking.audience.items', { returnObjects: true }) as string[];

  return (
    <Layout>
      {/* Success Animation Overlay */}
      <AnimatePresence>
        {showSuccess && <SuccessAnimation onClose={() => setShowSuccess(false)} t={t} />}
      </AnimatePresence>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary-light" />
        <div className="absolute inset-0 opacity-50" style={{ background: 'var(--gradient-mesh)' }} />
        <div className="absolute top-1/4 right-[10%] w-[400px] h-[400px] rounded-full bg-accent/15 blur-[100px] animate-float" />
        <div className="absolute inset-0 pattern-hero" />

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div 
            ref={heroRef}
            className={`max-w-3xl mx-auto text-center animate-on-scroll ${heroVisible ? 'is-visible' : ''}`}
          >
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 backdrop-blur-sm mb-6">
              <Calendar className="w-4 h-4 text-accent" />
              <span className="text-primary-foreground/90 text-sm font-semibold">{t('hero.badge')}</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              {t('hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-primary-foreground/75 leading-relaxed">
              {t('hero.subtitle')}
            </p>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
            <path d="M0 80L60 72C120 64 240 48 360 40C480 32 600 32 720 36C840 40 960 48 1080 52C1200 56 1320 56 1380 56L1440 56V80H0Z" fill="hsl(var(--background))" />
          </svg>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {/* Phone */}
            <a
              href="tel:+972528300088"
              className="group bg-card rounded-2xl p-6 border border-border shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 text-center"
            >
              <div className="w-14 h-14 mx-auto rounded-xl bg-gradient-to-br from-accent/15 to-accent/5 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Phone className="w-6 h-6 text-accent" />
              </div>
              <h3 className="font-heading font-bold text-lg text-foreground mb-2">{t('methods.phone.title')}</h3>
              <p dir="ltr" className="text-muted-foreground">{t('methods.phone.number')}</p>
            </a>

            {/* WhatsApp */}
            <a
              href="https://wa.me/972528300088?text=שלום,%20אשמח%20לקבל%20פרטים%20נוספים"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-card rounded-2xl p-6 border border-border shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 text-center"
            >
              <div className="w-14 h-14 mx-auto rounded-xl bg-gradient-to-br from-accent/15 to-accent/5 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <MessageCircle className="w-6 h-6 text-accent" />
              </div>
              <h3 className="font-heading font-bold text-lg text-foreground mb-2">{t('methods.whatsapp.title')}</h3>
              <p className="text-muted-foreground">{t('methods.whatsapp.subtitle')}</p>
            </a>

            {/* Email */}
            <a
              href="mailto:Support@Adam-Safety.com"
              className="group bg-card rounded-2xl p-6 border border-border shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 text-center"
            >
              <div className="w-14 h-14 mx-auto rounded-xl bg-gradient-to-br from-accent/15 to-accent/5 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Mail className="w-6 h-6 text-accent" />
              </div>
              <h3 className="font-heading font-bold text-lg text-foreground mb-2">{t('methods.email.title')}</h3>
              <p className="text-muted-foreground text-sm">{t('methods.email.address')}</p>
            </a>
          </div>
        </div>
      </section>

      {/* Book a Call Section */}
      <section className="py-16 md:py-24 section-alt">
        <div className="container mx-auto px-4 md:px-8">
          <div
            ref={bookingRef}
            className={`animate-on-scroll ${bookingVisible ? 'is-visible' : ''}`}
          >
            {/* Section Header */}
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="inline-block text-accent font-bold text-sm uppercase tracking-widest mb-4 px-4 py-2 rounded-full bg-accent/10 border border-accent/20">
                <Sparkles className="w-4 h-4 inline ml-2" />
                {t('booking.badge')}
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
                {t('booking.title')}
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                {t('booking.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start max-w-6xl mx-auto">
              {/* Left: Info */}
              <div className="order-2 lg:order-1">
                {/* What happens in the call */}
                <div className="bg-card rounded-2xl p-8 border border-border shadow-card mb-8">
                  <h3 className="font-heading font-bold text-xl text-foreground mb-6 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-accent" />
                    </div>
                    {t('booking.whatHappens.title')}
                  </h3>
                  <ul className="space-y-4">
                    {whatHappensItems.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                        <span className="text-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Duration & Audience */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-card rounded-2xl p-6 border border-border shadow-card">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-accent" />
                      </div>
                      <h4 className="font-heading font-bold text-lg text-foreground">{t('booking.duration.title')}</h4>
                    </div>
                    <p className="text-2xl font-heading font-bold text-accent">{t('booking.duration.value')}</p>
                    <p className="text-sm text-muted-foreground mt-1">{t('booking.duration.method')}</p>
                  </div>

                  <div className="bg-card rounded-2xl p-6 border border-border shadow-card">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                        <Users className="w-5 h-5 text-accent" />
                      </div>
                      <h4 className="font-heading font-bold text-lg text-foreground">{t('booking.audience.title')}</h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {audienceItems.map((item, index) => {
                        const icons = [Building2, HardHat, Factory];
                        const Icon = icons[index % icons.length];
                        return (
                          <span key={index} className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-secondary text-secondary-foreground">
                            <Icon className="w-3 h-3" />
                            {item}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Calendar Embed */}
              <div className="order-1 lg:order-2">
                <div className="bg-card rounded-3xl border border-border shadow-lg overflow-hidden">
                  <iframe
                    src="https://cal.com/Adam-Safety?embed=true&theme=light&layout=month_view"
                    style={{ width: "100%", height: "550px", border: "none" }}
                    title={t('booking.calendar.title')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Alternative CTA */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-4">
              {t('alternative.title')}
            </h3>
            <p className="text-muted-foreground text-lg mb-8">
              {t('alternative.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+972528300088">
                <Button variant="cta" size="lg" className="gap-2 w-full sm:w-auto">
                  <Phone className="w-5 h-5" />
                  {t('methods.phone.number')}
                </Button>
              </a>
              <a
                href="https://wa.me/972528300088?text=שלום,%20אשמח%20לקבל%20פרטים%20נוספים"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" size="lg" className="gap-2 w-full sm:w-auto">
                  <MessageCircle className="w-5 h-5" />
                  {t('alternative.whatsappButton')}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
